import {
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  useCreateProcurementSubmissionMutation,
  useProcurementSubmissionsItemsQuery,
  useProcurementSubmissionsQuery,
} from 'services/submissions/procurement-submission'
import { IProcurementRequirement } from 'types/lots'
import { ISubmission } from 'types/submissions'
import {
  mapProcurementSubmissionToPayload,
  mapToProcurementSubmissionDefaultValues,
  ProcurementSubmissionRequirement,
} from './map-to-form-values'
import { ProcurementSubmissionWorkrateTable } from './procurement-submission-workrate-table'
import { useQueryState } from 'react-router-use-location-state'
import { Loading } from 'components/Loading'
import { TablePagination } from 'components/TablePagination'
import _ from 'lodash/fp'
import { useMySubmissionsQuery } from 'services/submissions'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

type FieldValues = {
  procurementRequirement: ProcurementSubmissionRequirement
}

type Props = {
  procurementRequirement: IProcurementRequirement
  submission: ISubmission
  lotId: string
  hasDocDeadlinePassed: boolean
}

export const ProcurementSubmission = ({
  procurementRequirement,
  submission,
  lotId,
  hasDocDeadlinePassed,
}: Props) => {
  const { t } = useTranslation()
  const [page, setPage] = useQueryState('page', 0)
  const rowsPerPage = 10
  const { refetch: refetchMySubmission } = useMySubmissionsQuery(
    { lotId },
    { skip: !lotId }
  )

  const [isVatIncluded, setIsVatIncluded] = useState(
    !!submission?.procurementSubmission?.vatIncluded
  )

  const [createProcurementSubmission, { isSuccess, isError, isUninitialized }] =
    useCreateProcurementSubmissionMutation()

  const args = {
    page,
    size: rowsPerPage,
    id: submission?.procurementSubmission?.id!,
  } as const

  const {
    data,
    isLoading: procurementSubmissionItemsLoading,
    refetch,
  } = useProcurementSubmissionsItemsQuery(args, {
    skip: !submission,
    refetchOnMountOrArgChange: true,
  })

  const {
    data: procurementSubmission,
    isLoading: procurementSubmissionLoading,
    refetch: refetchProcSubmission,
  } = useProcurementSubmissionsQuery(
    { id: submission?.procurementSubmission?.id! },
    {
      skip: !submission,
    }
  )

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  const defaultFormValues = {
    procurementRequirement: mapToProcurementSubmissionDefaultValues({
      procurementRequirement: procurementRequirement,
      procurementSubmission: submission.procurementSubmission,
    }),
  }

  const methods = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  })

  const { control, handleSubmit, watch } = methods

  const onSubmit = async ({ procurementRequirement }: FieldValues) => {
    const body = mapProcurementSubmissionToPayload(
      procurementRequirement,
      submission
    )
    await createProcurementSubmission(body)
    void refetch()
    void refetchMySubmission()
    void refetchProcSubmission()
  }

  const requirementItems = watch(
    'procurementRequirement.procurementRequirementItems'
  )

  const isValid = requirementItems.every((item) => !_.isNil(item.price))

  if (procurementSubmissionItemsLoading || procurementSubmissionLoading) {
    return <Loading fullPage={false} />
  }

  if (data === undefined || procurementSubmission === undefined) {
    return null
  }

  return (
    <Stack>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CollapsibleSection title={t('price-proposal')}>
            <Stack spacing={3}>
              <TableContainer>
                <Table
                  sx={{
                    tableLayout: 'fixed',
                    '& td, th': {
                      border: '1px solid #ccc',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>{t('evaluationOfDocuments')}</StyledTableCell>
                      <StyledTableCell>
                        {t('contractorOfferAndNotes')}
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Stack spacing={3}>
                          <Controller
                            name="procurementRequirement.priceDescription"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('price-proposal-description')}
                                fullWidth
                                InputProps={{
                                  rows: 2,
                                  multiline: true,
                                  inputComponent: 'textarea',
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.validityDurationDays"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('tender-proposal-expiration-duration')}
                                fullWidth
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.guaranteeAmount"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('tender-proposal-warranty-amount')}
                                fullWidth
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.guaranteeValidityDurationDays"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('tender-proposal-validity-duration')}
                                fullWidth
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.executionGuarantee"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('warranty-of-procurement-contract-action')}
                                fullWidth
                                InputProps={{
                                  rows: 2,
                                  multiline: true,
                                  inputComponent: 'textarea',
                                }}
                              />
                            )}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={3}>
                          <Controller
                            name="procurementRequirement.commentOnOfferValue"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('comment')}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  rows: 2,
                                  multiline: true,
                                  inputComponent: 'textarea',
                                }}
                                disabled={hasDocDeadlinePassed}
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.validityDurationDays"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('tender-proposal-expiration-duration')}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.guaranteeAmount"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('tender-proposal-warranty-amount')}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.guaranteeValidityDurationDays"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                disabled
                                label={t('tender-proposal-validity-duration')}
                                fullWidth
                                variant="outlined"
                              />
                            )}
                          />
                          <Controller
                            name="procurementRequirement.commentOnOfferGuarantee"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={t('comment')}
                                fullWidth
                                variant="outlined"
                                InputProps={{
                                  rows: 2,
                                  multiline: true,
                                  inputComponent: 'textarea',
                                }}
                                disabled={hasDocDeadlinePassed}
                              />
                            )}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <ProcurementSubmissionWorkrateTable
                procurementSubmissionId={submission.procurementSubmission?.id}
                procurementSubmissionItems={data}
                rowsPerPage={rowsPerPage}
                isVatIncluded={isVatIncluded}
                hasDocDeadlinePassed={hasDocDeadlinePassed}
                procurementSubmission={procurementSubmission}
              />
              {data && data.info.totalRows !== 0 && (
                <TablePagination
                  total={data.info.totalRows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={setPage}
                />
              )}
              <Stack direction="row" justifyContent="flex-end">
                <FormControlLabel
                  control={
                    <Controller
                      name="procurementRequirement.vatIncluded"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => {
                            setIsVatIncluded((isVatIncluded) => !isVatIncluded)
                            field.onChange(e.target.checked)
                          }}
                          disabled={hasDocDeadlinePassed}
                        />
                      )}
                    />
                  }
                  label={t('includeVAT')}
                />
              </Stack>
            </Stack>
          </CollapsibleSection>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              type="submit"
              variant="contained"
              style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
              onClick={handleSubmit(onSubmit)}
              disabled={
                !isValid ||
                hasDocDeadlinePassed ||
                (!methods.formState.isDirty && isUninitialized)
              }
            >
              {t('save')}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  )
}
